import {useEffect, useState} from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseIcon from "@mui/icons-material/Close";
import {useLocale, useMessages} from "i18n";
import {useUser} from "UserProvider";
import {formatDateMed} from "core/format/dateFormat";
import {formatMinorMoney} from "pricing/PricingStorage";
import {requestBillingSheet} from "account/billing/billingSheetRequest";
import {
	allowanceCoverageOf,
	coverageSentence,
	spendablePurchasesOf,
	type UnusedPurchase,
	unusedPurchaseNoteVisible,
	usingPurchaseOf,
} from "account/purchases/unusedPurchases";
import {
	purchasesSeenAtDismissal,
	recordNoteDismissal,
} from "account/purchases/unusedPurchaseNoteDismissal";
import {startUsingPurchase, stopUsingPurchase} from "account/purchases/purchaseUseIntent";
import {usePurchaseUseIntent} from "account/purchases/usePurchaseUseIntent";
import {useUnusedPurchases} from "account/purchases/useUnusedPurchases";
import {
	DismissButton, NoteItem, NoteLink, NoteLinks, NoteList, NoteRoot, NoteTitle, UsingRoot,
} from "./UnusedPurchasesNotice.styles";

/**
 * The unused-purchase note in the create panel's notices slot, and the strip that replaces it after
 * "Use it" (kitecorp/transferify-ui#3004, api kitecorp/transferify-api-service#2196).
 *
 * <b>Why the note exists beside the Billing card.</b> A sender who paid for a send over the limit and then
 * sent something smaller lands on the home page next time, not in Billing; the card there is where the
 * withdrawal function lives, and this is the same purchase on the surface they actually use. It offers only the
 * purchases a transfer can spend (`spendablePurchasesOf`): a credit listed for its withdrawal is not one.
 *
 * <b>Signed-in only</b>, and gated by the outer component rather than inside the hook: the listing is an
 * authenticated read, and mounting the hook for a visitor would record a 401 as a failed load.
 *
 * <b>Behaviour with files picked.</b> The slot's other notices (the verify callout, the offer callouts)
 * render whether or not files are staged, and this one does the same rather than hiding on the first file:
 * a reader who picks an over-cap file first still has the note explaining why the strip below reads
 * "Paid" instead of a price.
 *
 * <b>"Use it" is a choice the upload honours.</b> The intent carries the purchase into the composer,
 * where `preferredOverLimitAllowance` lets it stand in for the api's best-fit candidate on the upload
 * init wherever the listing proves the api would take it.
 *
 * Markup and controls only: whether the note shows (`unusedPurchaseNoteVisible`), what was seen when it
 * was closed (`unusedPurchaseNoteDismissal`), and which purchase is being used (`purchaseUseIntent`) are
 * React-free modules under `account/purchases`.
 */
export function UnusedPurchasesNotice() {
	const [user] = useUser();
	if (!user.isLoggedIn) {
		return null;
	}
	return <SignedInUnusedPurchasesNotice accountId={user.id}/>;
}

function SignedInUnusedPurchasesNotice({accountId}: {accountId: string | null}) {
	const {account, sender} = useMessages();
	const {locale} = useLocale();
	const listing = useUnusedPurchases();
	const loaded = listing.loaded;
	// A personalization credit is listed for its withdrawal and can be spent on nothing: the note offers only what a
	// transfer can use.
	const purchases = spendablePurchasesOf(listing.purchases);
	const usingId = usePurchaseUseIntent().purchase?.purchaseId ?? null;
	// Read once per mount, and again after this mount's own dismissal: the record is per browser, so
	// nothing else moves it while the panel is on screen.
	const [seenAtDismissal, setSeenAtDismissal] = useState(() => purchasesSeenAtDismissal(accountId));
	const copy = sender.unusedPurchases;
	const using = usingPurchaseOf(purchases, usingId);

	// "Use it" on a purchase that has since been spent or refunded has nothing left to use; the strip
	// goes and the note's own rule decides what shows next. Only once the listing has answered: an
	// unloaded list is not evidence the purchase is gone.
	useEffect(() => {
		if (loaded && usingId !== null && using === null) {
			stopUsingPurchase();
		}
	}, [loaded, usingId, using]);

	const coverageOf = (purchase: UnusedPurchase) =>
		coverageSentence(allowanceCoverageOf(purchase, locale), account.unusedPurchases.covers);

	if (using !== null) {
		return (
			<UsingRoot role="status" data-testid="unused-purchase-using">
				<span>
					<b>{copy.using.overLimit}</b>
					{" "}
					{copy.using.overLimitDetail(coverageOf(using))}
				</span>
				<NoteLink onClick={stopUsingPurchase} data-testid="unused-purchase-stop-using">
					{copy.using.stopOverLimit}
				</NoteLink>
			</UsingRoot>
		);
	}

	if (!unusedPurchaseNoteVisible(purchases, seenAtDismissal, usingId)) {
		return null;
	}

	const dismiss = () => {
		const seen = purchases.map(purchase => purchase.purchaseId);
		recordNoteDismissal(accountId, seen);
		setSeenAtDismissal(seen);
	};
	const useIt = (purchase: UnusedPurchase) => startUsingPurchase(purchase);
	const priceOf = (purchase: UnusedPurchase) => formatMinorMoney(purchase.currency, purchase.amountMinor, locale);
	const dateOf = (purchase: UnusedPurchase) => formatDateMed(purchase.boughtAt, locale);

	return (
		<NoteRoot role="status" data-testid="unused-purchases-note">
			{purchases.length === 1 ? (
				<>
					<NoteTitle>{copy.note.oneOverLimitTitle}</NoteTitle>
					<span>{copy.note.oneOverLimitBody(coverageOf(purchases[0]), priceOf(purchases[0]), dateOf(purchases[0]))}</span>
					<NoteLinks>
						<NoteLink onClick={() => useIt(purchases[0])} data-testid="unused-purchase-note-use"
								  data-purchase-id={purchases[0].purchaseId}>
							{account.unusedPurchases.useIt}
							<ArrowForwardRoundedIcon fontSize="inherit" aria-hidden="true"/>
						</NoteLink>
						<NoteLink $muted onClick={requestBillingSheet} data-testid="unused-purchases-note-billing">
							{copy.note.billingLink}
						</NoteLink>
					</NoteLinks>
				</>
			) : (
				<>
					<NoteTitle>{copy.note.manyTitle(purchases.length)}</NoteTitle>
					<NoteList>
						{purchases.map(purchase => (
							<NoteItem key={purchase.purchaseId}>
								<span>{copy.note.itemOverLimit(priceOf(purchase))}</span>
								<NoteLink onClick={() => useIt(purchase)} data-testid="unused-purchase-note-use"
										  data-purchase-id={purchase.purchaseId}>
									{account.unusedPurchases.useIt}
									<ArrowForwardRoundedIcon fontSize="inherit" aria-hidden="true"/>
								</NoteLink>
							</NoteItem>
						))}
					</NoteList>
					<NoteLinks>
						<NoteLink $muted onClick={requestBillingSheet} data-testid="unused-purchases-note-billing">
							{copy.note.billingLink}
						</NoteLink>
					</NoteLinks>
				</>
			)}
			<DismissButton size="small" aria-label={copy.note.closeAria} onClick={dismiss} data-testid="unused-purchases-note-close">
				<CloseIcon fontSize="small"/>
			</DismissButton>
		</NoteRoot>
	);
}
